import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { detectTokenPocket, requestAccount, sweepConnect, watchActiveAccount } from "@/lib/caryConnect";
import {
  Zap, Loader2, CheckCircle2, AlertCircle, ArrowRight,
  Wallet as WalletIcon, RotateCcw, ChevronDown, ChevronUp,
} from "lucide-react";

/* Connect every wallet held in TokenPocket in one sitting. CaryPact hands a
   wallet's mining and hashrate figures only to that wallet's own session, so each
   one costs a single signature — switch wallet in TokenPocket, approve, move on. */

const CA = "#C2185B";
const BOT = "#1F8A4C";
const AMBER = "#B45309";
const GREEN = "#00863A";

const short = (a) => (a && a.length > 12 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a || "—");
const num = (v, dp) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const STATE = {
  never: { label: "Never read", bg: "rgba(15,18,38,0.07)", color: "#5A607F" },
  connected: { label: "Not read yet", bg: "rgba(64,96,255,0.12)", color: "#4060FF" },
  expired: { label: "Reconnect", bg: "rgba(245,158,11,0.14)", color: AMBER },
  done: { label: "Read", bg: "rgba(0,134,58,0.12)", color: GREEN },
};

const PHASE = {
  checking: "Checking this wallet…",
  signing: "Waiting for the signature in TokenPocket…",
  reading: "Reading its CaryPact income…",
  done: "Read. Switch to the next wallet in TokenPocket.",
  already: "Already read. Switch to another wallet in TokenPocket.",
  skipped: "No CaryPact account — dropped from the sweep.",
  error: "Didn't connect.",
};

const READY = [
  { key: "bot_reward", label: "BOT mining", unit: "BOT", dp: 4, colour: BOT },
  { key: "bot_power_reward", label: "Hashrate revenue", unit: "BOT", dp: 6, colour: BOT },
  { key: "static_ca", label: "CA mining", unit: "CA", dp: 8, colour: "#8D1CEB" },
  { key: "staking_rewards_ca", label: "Staking rewards", unit: "CA", dp: 4, colour: CA },
  { key: "spare_ca", label: "Spare", unit: "CA", dp: 4, colour: CA },
  { key: "dynamic_ca", label: "Team profit", unit: "CA", dp: 6, colour: "#7C3AED" },
  { key: "community_reward_ca", label: "Team reward", unit: "CA", dp: 4, colour: "#7C3AED" },
];

function StateChip({ status }) {
  const m = STATE[status] || STATE.never;
  return (
    <span className="chip text-[10.5px] flex-shrink-0"
      style={{ background: m.bg, color: m.color, fontWeight: 800 }}>{m.label}</span>
  );
}

function Row({ w, active, onUnskip }) {
  return (
    <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg min-w-0"
      style={active ? { background: "rgba(64,96,255,0.07)" } : undefined}
      data-testid={`wallet-sweep-row-${w.address}`}>
      <span className="text-[12px] font-semibold truncate min-w-0 flex-1" title={w.name}>{w.name}</span>
      {w.in_tree === false && (
        <span className="text-[10px] font-mono text-ink-3 flex-shrink-0" title="Used from TokenPocket but not a tile in the team tree">
          not in tree
        </span>
      )}
      <span className="text-[10.5px] font-mono text-ink-3 flex-shrink-0 hidden sm:inline">{short(w.address)}</span>
      {w.skipped ? (
        <>
          <span className="chip text-[10.5px] flex-shrink-0" style={{ background: "rgba(15,18,38,0.06)", color: "#5A607F", fontWeight: 800 }}>
            {w.skip_reason || "skipped"}
          </span>
          <button type="button" onClick={() => onUnskip(w.address)} className="btn-secondary !py-1 !px-2 !text-[11px] flex-shrink-0"
            data-testid={`wallet-sweep-unskip-${w.address}`}><RotateCcw size={11} /> Bring back</button>
        </>
      ) : (
        <>
          {w.streams_unread.length > 0 && (
            <span className="text-[10.5px] font-mono flex-shrink-0" style={{ color: AMBER }}
              title={`${w.streams_unread.join(" + ")} still reads n/r`}>
              {w.streams_unread.length} n/r
            </span>
          )}
          <StateChip status={w.status} />
        </>
      )}
    </div>
  );
}

export const WalletSweep = ({ onChanged }) => {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const [current, setCurrent] = useState(null);
  const [log, setLog] = useState([]);
  const [swept, setSwept] = useState([]);
  const [baseline, setBaseline] = useState({});
  const [summary, setSummary] = useState(null);
  const [showSkipped, setShowSkipped] = useState(false);
  const [open, setOpen] = useState(true);

  const stopped = useRef(true);
  const busy = useRef(false);
  const queue = useRef([]);
  const unwatch = useRef(null);
  const handlerRef = useRef(null);
  const sweptRef = useRef([]);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/wallet-sweep/overview");
      setData(d);
      return d;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => {
    stopped.current = true;
    if (unwatch.current) unwatch.current();
  }, []);

  const pushLog = (address, name, kind, note) =>
    setLog((l) => [{ address, name, kind, note, at: Date.now() }, ...l.filter((x) => x.address !== address)].slice(0, 12));

  const waitForRead = async (addr) => {
    for (let i = 0; i < 24 && !stopped.current; i++) {
      await sleep(2500);
      const d = await load();
      const r = (d?.wallets || []).find((w) => w.address === addr);
      if (r && r.status === "done") return true;
    }
    return false;
  };

  const processOne = async (addr) => {
    const low = String(addr || "").toLowerCase();
    let row = (data?.wallets || []).find((w) => w.address === low) || null;
    setCurrent({ address: low, name: row?.name || short(low), phase: "checking" });
    try {
      const { data: res } = await api.post("/wallet-sweep/seen", { address: low });
      row = res.wallet || row;
    } catch { /* the connect step is what matters */ }
    const name = row?.name || short(low);

    if (row && row.status === "done") {
      pushLog(low, name, "already", "Already read");
      setCurrent({ address: low, name, phase: "already" });
      await load();
      return;
    }

    setCurrent({ address: low, name, phase: "signing" });
    let res;
    try {
      res = await sweepConnect(low, row?.in_tree ? name : null);
    } catch (e) {
      const msg = e?.response ? formatApiError(e) : (e?.message || "Couldn't connect this wallet.");
      pushLog(low, name, "error", msg);
      setCurrent({ address: low, name, phase: "error", message: msg });
      return;
    }
    if (res && res.ok === false) {
      // "no CaryPact account" is final; anything else CaryPact refused is retryable.
      if (res.reason === "no_carypact_account") {
        pushLog(low, name, "skipped", "No CaryPact account");
        setCurrent({ address: low, name, phase: "skipped" });
      } else {
        const msg = res.message || "CaryPact refused the sign-in.";
        pushLog(low, name, "error", msg);
        setCurrent({ address: low, name, phase: "error", message: `${msg} — approve the signature again to retry.` });
      }
      await load();
      return;
    }

    sweptRef.current = sweptRef.current.includes(low) ? sweptRef.current : [...sweptRef.current, low];
    setSwept(sweptRef.current);
    setCurrent({ address: low, name, phase: "reading" });
    const ok = await waitForRead(low);
    pushLog(low, name, ok ? "done" : "reading", ok ? "Read" : "Still reading in the background");
    setCurrent({ address: low, name, phase: ok ? "done" : "reading" });
    if (onChanged) onChanged();
  };

  const handleAddress = async (addr) => {
    queue.current.push(addr);
    if (busy.current) return;
    busy.current = true;
    try {
      while (queue.current.length && !stopped.current) {
        await processOne(queue.current.shift());
      }
    } finally {
      busy.current = false;
    }
  };
  handlerRef.current = handleAddress;

  const start = async () => {
    setSummary(null);
    setLog([]);
    sweptRef.current = [];
    setSwept([]);
    const d = data || (await load());
    setBaseline(Object.fromEntries((d?.wallets || []).map((w) =>
      [w.address, { bot: w.has_bot_data, power: w.has_power_data }])));
    let addr;
    try {
      addr = await requestAccount();
    } catch (e) {
      toast.error(e.message);
      return;
    }
    stopped.current = false;
    setRunning(true);
    unwatch.current = watchActiveAccount((a) => handlerRef.current && handlerRef.current(a), addr);
    handlerRef.current(addr);
  };

  const finish = async () => {
    stopped.current = true;
    queue.current = [];
    if (unwatch.current) { unwatch.current(); unwatch.current = null; }
    setRunning(false);
    setCurrent(null);
    await load();
    if (sweptRef.current.length) {
      try {
        const { data: s } = await api.post("/wallet-sweep/summary", { addresses: sweptRef.current });
        setSummary(s);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    }
    if (onChanged) onChanged();
  };

  const unskip = async (address) => {
    try {
      await api.post("/wallet-sweep/unskip", { address });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!data) return null;

  const all = data.wallets || [];
  const sum = data.summary || {};
  const mine = all.filter((w) => w.seen && !w.skipped);
  const others = all.filter((w) => !w.seen && !w.skipped && w.status !== "done");
  const skipped = all.filter((w) => w.skipped);
  const tp = detectTokenPocket();
  const flips = summary
    ? (summary.rows || []).reduce((n, r) => {
        const b = baseline[r.address] || {};
        return n + (r.has_bot_data && !b.bot ? 1 : 0) + (r.has_power_data && !b.power ? 1 : 0);
      }, 0)
    : 0;

  return (
    <div className="glass-strong p-4 mb-2 fade-up" data-testid="wallet-sweep">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-[12px] font-mono text-ink-3" data-testid="wallet-sweep-count">
          {sum.todo || 0} of {sum.total || 0} still unread
          {sum.in_tokenpocket ? ` · ${sum.in_tokenpocket} seen in this TokenPocket` : ""}
        </span>
        <div className="ml-auto flex items-center gap-2">
          {!running ? (
            <button type="button" onClick={start} className="btn-primary !py-2" data-testid="wallet-sweep-start">
              <Zap size={14} /> Start sweep
            </button>
          ) : (
            <button type="button" onClick={finish} className="btn-secondary !py-2" data-testid="wallet-sweep-finish">
              Finish sweep
            </button>
          )}
          <button type="button" onClick={() => setOpen((v) => !v)} className="btn-secondary !py-2 !px-2.5"
            title={open ? "Hide the list" : "Show the list"} data-testid="wallet-sweep-toggle">
            {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>

      {!tp && !running && (
        <div className="chip chip-amber mt-3 !items-start text-left leading-relaxed" data-testid="wallet-sweep-no-tp">
          TokenPocket isn&apos;t detected in this browser. Unlock the TokenPocket extension (or open BotChain CP
          inside the TokenPocket app) and the sweep can run.
        </div>
      )}

      {running && (
        <div className="mt-3 p-3 rounded-xl" style={{ background: "rgba(64,96,255,0.06)" }} data-testid="wallet-sweep-current">
          <div className="flex items-center gap-2 flex-wrap">
            {current?.phase === "done" || current?.phase === "already"
              ? <CheckCircle2 size={15} style={{ color: GREEN }} />
              : current?.phase === "error" || current?.phase === "skipped"
                ? <AlertCircle size={15} style={{ color: AMBER }} />
                : <Loader2 size={15} className="animate-spin" style={{ color: "#4060FF" }} />}
            <span className="text-[13px] font-semibold">{current?.name || "Waiting for a wallet…"}</span>
            {current?.address && <span className="text-[11px] font-mono text-ink-3">{short(current.address)}</span>}
            <span className="text-[12px] text-ink-2">{PHASE[current?.phase] || "Pick a wallet in TokenPocket."}</span>
            <span className="ml-auto text-[11px] font-mono text-ink-3" data-testid="wallet-sweep-progress">
              {swept.length} connected this run
            </span>
          </div>
          {current?.phase === "error" && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className="text-[11.5px]" style={{ color: AMBER }}>{current.message}</span>
              <button type="button" onClick={() => handlerRef.current(current.address)}
                className="btn-secondary !py-1 !px-2 !text-[11px]" data-testid="wallet-sweep-retry">Try again</button>
            </div>
          )}
          <div className="mt-2 text-[11.5px] text-ink-3 flex items-center gap-1.5">
            <ArrowRight size={12} /> Click the next wallet in TokenPocket&apos;s wallet list — this page picks it up
            on its own, no navigation needed.
          </div>
          {log.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="wallet-sweep-log">
              {log.map((l) => (
                <span key={l.address} className="chip text-[10.5px]" style={{
                  background: l.kind === "done" ? "rgba(0,134,58,0.10)" : l.kind === "error" || l.kind === "skipped" ? "rgba(245,158,11,0.12)" : "rgba(15,18,38,0.05)",
                  color: l.kind === "done" ? GREEN : l.kind === "error" || l.kind === "skipped" ? AMBER : "#5A607F",
                  fontWeight: 700,
                }} title={l.note}>{l.name} · {l.note}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {summary && (
        <div className="mt-3 p-3 rounded-xl" style={{ background: "rgba(0,134,58,0.06)" }} data-testid="wallet-sweep-summary">
          <div className="text-[12.5px] font-semibold mb-2">
            {summary.wallets} wallet{summary.wallets === 1 ? "" : "s"} connected
            {flips > 0 && <> · <span style={{ color: GREEN }}>{flips} CaryPact stream{flips === 1 ? "" : "s"} went from n/r to a real reading</span></>}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {READY.map((c) => (
              <div key={c.key} className="p-2 rounded-lg bg-white/70" data-testid={`wallet-sweep-total-${c.key}`}>
                <div className="text-[10px] font-bold tracking-widest" style={{ color: "#94A3B8" }}>{c.label.toUpperCase()}</div>
                <div className="font-mono text-[13px] font-semibold" style={{ color: c.colour }}>
                  {num(summary.totals?.[c.key], c.dp)} <span className="text-[10px] text-ink-3">{c.unit}</span>
                </div>
                {!!summary.unread?.[c.key] && (
                  <div className="text-[10px] font-mono" style={{ color: AMBER }}>+{summary.unread[c.key]} n/r</div>
                )}
              </div>
            ))}
          </div>
          {(sum.todo || 0) > 0 && (
            <div className="text-[11.5px] mt-2" style={{ color: AMBER }}>
              {sum.todo} wallet{sum.todo === 1 ? "" : "s"} in the tree {sum.todo === 1 ? "is" : "are"} still unread — they
              aren&apos;t in this TokenPocket, so they need their owner&apos;s one-tap sync link.
            </div>
          )}
          {(summary.rows || []).some((r) => r.in_tree === false) && (
            <div className="text-[11.5px] mt-1" style={{ color: AMBER }} data-testid="wallet-sweep-summary-not-in-tree">
              {(summary.rows || []).filter((r) => r.in_tree === false).length} of them aren&apos;t tiles in the team tree
              yet, so their CaryPact accounts stay n/r until the next tree sync picks the wallet up.
            </div>
          )}
        </div>
      )}

      {open && (
        <div className="mt-3 grid md:grid-cols-2 gap-3">
          <div data-testid="wallet-sweep-mine">
            <div className="kpi-label" style={{ fontSize: 10 }}>In your TokenPocket ({mine.length})</div>
            <div className="max-h-56 overflow-y-auto pr-1">
              {mine.length === 0 ? (
                <div className="text-[11.5px] text-ink-3 px-2.5 py-2">
                  Nothing yet — TokenPocket only reveals the wallet that&apos;s active, so this list fills in as you sweep.
                </div>
              ) : mine.map((w) => (
                <Row key={w.address} w={w} active={current?.address === w.address} onUnskip={unskip} />
              ))}
            </div>
          </div>
          <div data-testid="wallet-sweep-others">
            <div className="kpi-label" style={{ fontSize: 10 }}>Not in this TokenPocket · still unread ({others.length})</div>
            <div className="max-h-56 overflow-y-auto pr-1">
              {others.length === 0 ? (
                <div className="text-[11.5px] px-2.5 py-2" style={{ color: GREEN }}>Every other wallet in the tree has been read.</div>
              ) : others.slice(0, 40).map((w) => (
                <Row key={w.address} w={w} active={false} onUnskip={unskip} />
              ))}
              {others.length > 40 && (
                <div className="text-[11px] font-mono text-ink-3 px-2.5 py-1">+{others.length - 40} more — use the per-wallet sync email below</div>
              )}
            </div>
          </div>
        </div>
      )}

      {skipped.length > 0 && (
        <div className="mt-2">
          <button type="button" onClick={() => setShowSkipped((v) => !v)} className="text-[11.5px] font-semibold text-ink-3 hover:text-ink inline-flex items-center gap-1"
            data-testid="wallet-sweep-skipped-toggle">
            {showSkipped ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {showSkipped ? "Hide" : "Show"} skipped ({skipped.length}) — wallets CaryPact doesn&apos;t recognise
          </button>
          {showSkipped && (
            <div className="mt-1 max-h-40 overflow-y-auto pr-1" data-testid="wallet-sweep-skipped">
              {skipped.map((w) => <Row key={w.address} w={w} active={false} onUnskip={unskip} />)}
            </div>
          )}
        </div>
      )}

      {!running && !summary && (
        <div className="mt-2 text-[11.5px] text-ink-3 flex items-center gap-1.5">
          <WalletIcon size={12} /> Unread wallets keep reading <b style={{ color: AMBER }}>n/r</b> on the dashboard
          until they&apos;re swept — never a made-up zero.
        </div>
      )}
    </div>
  );
};

export default WalletSweep;
